import type { TelemetryPort } from '@core/domain/ports/driven/TelemetryPort.ts';
import type { AppMaintenancePort, HardRefreshOptions } from '../domain/ports/AppMaintenancePort.ts';
import type { SystemDiagnosticsPort, SystemHealthSnapshot } from '../domain/ports/SystemDiagnosticsPort.ts';
import type { SyncDownloadProgress, SyncPushProgress } from '@core/domain/sync/SyncPushResult.ts';
import {
  readResilienceLastExportAt,
  readResilienceLastPushSuccessAt,
  recordResiliencePushSuccess,
} from '../domain/resilienceTimestamps.ts';
import type { SyncOutboundPort } from '../domain/ports/SyncOutboundPort.ts';
import type {
  SyncPullPort,
  SyncReferenceMergePort,
  SyncStateRepositoryPort,
  SyncStatus,
} from '../domain/ports/SyncPorts.ts';
import {
  clearRecoveryBackupPresent,
  isRecoveryMergeFailed,
  recoveryMergeFailedBannerMessage,
  shouldBlockHardRefreshClearIdb,
} from '@src/modules/platform/local-sqlite/domain/recoveryMergeFlags.ts';

/** System use-cases: sync pull, diagnostics, and hard refresh. */
export class SystemApplicationService {
  constructor(
    private readonly pullPort: SyncPullPort,
    private readonly syncState: SyncStateRepositoryPort,
    private readonly referenceMerge: SyncReferenceMergePort | null,
    private readonly diagnostics: SystemDiagnosticsPort,
    private readonly maintenance: AppMaintenancePort,
    private readonly telemetry: TelemetryPort,
    private readonly remoteAvailable: boolean,
    private readonly syncOutbound: SyncOutboundPort,
    private readonly flushPersist?: () => Promise<void>,
  ) {}

  async loadSyncPending() {
    return this.syncOutbound.listPending();
  }

  async getStatus(): Promise<SyncStatus> {
    return this.syncState.getStatus(this.remoteAvailable);
  }

  async pullNow(opts?: {
    repopulate?: boolean;
    onDownloadProgress?: (progress: SyncDownloadProgress) => void;
  }): Promise<{ mergedCount: number }> {
    if (!this.remoteAvailable) {
      throw new Error('Sincronización no disponible: no hay servidor remoto configurado.');
    }
    const repopulate = opts?.repopulate === true;
    const span = this.telemetry.startTrace('sync.pull');
    try {
      const result = await this.pullPort.pull({ onDownloadProgress: opts?.onDownloadProgress });
      const sections = result.sections;
      if (!sections || Object.keys(sections).length === 0) {
        throw new Error('No se recibieron catálogos del servidor.');
      }
      const mergedCount = this.referenceMerge
        ? repopulate
          ? await this.referenceMerge.repopulate(sections)
          : await this.referenceMerge.merge(sections)
        : 0;
      await this.syncState.recordSuccess();
      if ((repopulate || mergedCount > 0) && this.flushPersist) await this.flushPersist();
      this.telemetry.trackEvent('sync.pull.success', { mergedCount, repopulate });
      span.end();
      return { mergedCount };
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      await this.syncState.recordFailure(message);
      this.telemetry.logError(e instanceof Error ? e : message, { source: 'sync.pull' });
      span.fail(e instanceof Error ? e : new Error(message));
      throw e;
    }
  }

  /** Push pending entregas/reposiciones to the server. Errors propagate. */
  async pushNow(opts?: { onProgress?: (progress: SyncPushProgress) => void }): Promise<{
    pushedEntregas: number;
    pushedReposiciones: number;
    entregaMediaPurgeSkipped: number;
    reposicionMediaPurgeSkipped: number;
    acceptedInferredFromTotales: boolean;
  }> {
    const {
      entregas,
      reposiciones,
      entregaMediaPurgeSkipped,
      reposicionMediaPurgeSkipped,
      acceptedInferredFromTotales,
    } = await this.syncOutbound.pushAll(opts);
    if (entregaMediaPurgeSkipped > 0) {
      this.telemetry.trackEvent('sync.push.entrega_media_purge_skipped', { count: entregaMediaPurgeSkipped });
    }
    if (reposicionMediaPurgeSkipped > 0) {
      this.telemetry.trackEvent('sync.push.media_purge_skipped', { count: reposicionMediaPurgeSkipped });
    }
    if (acceptedInferredFromTotales) {
      this.telemetry.trackEvent('sync.push.accepted_inferred_from_totales', {
        entregas,
        reposiciones,
      });
      this.telemetry.logInfo('Sync push: acceptedPublicIds inferred from server totales', {
        source: 'sync.push',
        entregas,
        reposiciones,
      });
    }
    if (entregas + reposiciones > 0) recordResiliencePushSuccess();
    return {
      pushedEntregas: entregas,
      pushedReposiciones: reposiciones,
      entregaMediaPurgeSkipped,
      reposicionMediaPurgeSkipped,
      acceptedInferredFromTotales,
    };
  }

  /** Pull catalogs; push errors propagate. Pull failure is captured, not thrown. */
  async syncAll(opts?: {
    /** Default false (merge). Use true after app update or manual full reload. */
    repopulate?: boolean;
    onPushStart?: () => void;
    onPushProgress?: (progress: SyncPushProgress) => void;
    onDownloadProgress?: (progress: SyncDownloadProgress) => void;
  }): Promise<{
    mergedCount: number;
    pullError: string | null;
    pushedEntregas: number;
    pushedReposiciones: number;
    entregaMediaPurgeSkipped: number;
    reposicionMediaPurgeSkipped: number;
  }> {
    let mergedCount = 0;
    let pullError: string | null = null;
    const repopulate = opts?.repopulate === true;
    try {
      ({ mergedCount } = await this.pullNow({
        repopulate,
        onDownloadProgress: opts?.onDownloadProgress,
      }));
    } catch (e) {
      pullError = e instanceof Error ? e.message : String(e);
    }
    const pending = await this.loadSyncPending();
    const pendingCount = pending.entregas.length + pending.reposiciones.length;
    if (pendingCount > 0) opts?.onPushStart?.();
    return { mergedCount, pullError, ...(await this.pushNow({ onProgress: opts?.onPushProgress })) };
  }

  async getHealthSnapshot(): Promise<SystemHealthSnapshot> {
    const sync = await this.syncState.getStatus(this.remoteAvailable);
    const snapshot = await this.diagnostics.collectHealth(this.remoteAvailable);
    const merged: SystemHealthSnapshot = {
      ...snapshot,
      sync: {
        lastPullAt: sync.lastPullAt,
        lastError: sync.lastError,
        remoteAvailable: sync.remoteAvailable,
      },
      resilience: {
        lastPushSuccessAt: readResilienceLastPushSuccessAt(),
        lastExportAt: readResilienceLastExportAt(),
      },
    };
    this.telemetry.trackEvent('system.diagnostics.viewed', {
      platform: merged.platform,
      online: merged.online,
      productosReposicion: merged.catalogs.productosReposicion,
    });
    return merged;
  }

  copyDiagnostics(snapshot: SystemHealthSnapshot): string {
    return this.diagnostics.copyDiagnosticsJson(snapshot);
  }

  getPlatform() {
    return this.maintenance.getPlatform();
  }

  getMobileStoreUrl(): string | null {
    return this.maintenance.getMobileStoreUrl();
  }

  getLocalAppVersion() {
    return this.maintenance.getLocalAppVersion();
  }

  checkForAppUpdate() {
    return this.maintenance.checkForAppUpdate();
  }

  async hardRefresh(options: HardRefreshOptions): Promise<void> {
    if (options.clearIndexedDb && shouldBlockHardRefreshClearIdb()) {
      throw new Error(
        'Hay un respaldo local pendiente. Exporte en Observabilidad antes de borrar datos locales.',
      );
    }
    this.telemetry.trackEvent('system.hard_refresh.start', {
      platform: this.maintenance.getPlatform(),
      clearIndexedDb: options.clearIndexedDb,
    });
    await this.maintenance.hardRefresh(options);
  }

  async countOrphanMediaBlobs(): Promise<number> {
    return this.diagnostics.countOrphanMediaBlobs();
  }

  async purgeOrphanMediaBlobs(): Promise<number> {
    const deleted = await this.diagnostics.purgeOrphanMediaBlobs();
    this.telemetry.trackEvent('system.orphan_media.purged', { deleted });
    return deleted;
  }

  recoveryMergeFailedBanner(): string | null {
    return recoveryMergeFailedBannerMessage();
  }

  isRecoveryMergeFailed(): boolean {
    return isRecoveryMergeFailed();
  }

  clearRecoveryBackupAfterExport(): void {
    clearRecoveryBackupPresent();
  }
}
